import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  Select,
  MenuItem,
  Button,
  TextField,
  InputLabel,
  FormControl,
  Chip
} from "@mui/material";
import { grey } from "@mui/material/colors";
import DiscoverySequenceConfigV2 from "./DiscoverySequenceConfigV2";
import { updateDiscoverySequences } from "../utils/api";

const sections = [
  { label: "Agent Prompts", value: "Prompts" },
  { label: "Discovery Sequences", value: "DiscoverySequences" },
  { label: "Personalization", value: "Personalization" },
  { label: "Speech", value: "Speech" },
  { label: "Data controls", value: "DataControls" },
  { label: "Connected apps & automation", value: "ConnectedApps" },
  { label: "Security", value: "Security" },
];


const DEV_MODE = true;
const url_dev = 'http://intakemate-api.findem.ai:3001'
const url_prod = 'https://intakemate-api.findem.ai'
const api_url = DEV_MODE ? url_dev : url_prod;

const SettingsMenu = ({ onClose, userId }) => {
  const [activeSection, setActiveSection] = useState("Prompts");
  const [selectedPrompt, setSelectedPrompt] = useState("Default");
  const [promptText, setPromptText] = useState(
    "Welcome to your agent! Customize this prompt as needed."
  );
  const [promptTemplates, setPromptTemplates] = useState([
    { label: "Default", value: "Default", isDefault: true, text: "Welcome to your agent! Customize this prompt as needed." },
    { label: "Customer Support", value: "Customer Support", isDefault: false, text: "Hello! How can I assist you today?" },
    { label: "Sales Agent", value: "Sales Agent", isDefault: false, text: "Looking for our products? Let me guide you." },
    { label: "Technical Assistance", value: "Technical Assistance", isDefault: false, text: "Let me help you with your technical issues." },
  ]);

  
  // const handlePromptChange = (event) => {
  //   const selected = event.target.value;
  //   setSelectedPrompt(selected);

  //   const selectedTemplate = promptTemplates.find(
  //     (template) => template.value === selected
  //   );
  //   if (selectedTemplate) {
  //     setPromptText(selectedTemplate.text);
  //   }
  // };

  // const handleSavePrompt = () => {
  //   setPromptTemplates((prevTemplates) =>
  //     prevTemplates.map((template) =>
  //       template.value === selectedPrompt
  //         ? { ...template, text: promptText }
  //         : template
  //     )
  //   );
  //   alert("Prompt saved successfully!");
  // };

  // const handleCreateNewPrompt = () => {
  //   const newPromptLabel = `New Prompt ${promptTemplates.length + 1}`;
  //   const newPromptValue = `NewPrompt${promptTemplates.length + 1}`;
  //   setPromptTemplates((prevTemplates) => [
  //     ...prevTemplates,
  //     { label: newPromptLabel, value: newPromptValue, isDefault: false, text: "" },
  //   ]);
  //   setSelectedPrompt(newPromptValue);
  //   setPromptText("Customize your new prompt here.");
  // };

  // const handleSetDefault = () => {
  //   setPromptTemplates((prevTemplates) =>
  //     prevTemplates.map((template) => ({
  //       ...template,
  //       isDefault: template.value === selectedPrompt,
  //     }))
  //   );
  //   alert(`${selectedPrompt} is now the default prompt!`);
  // };
  const handleDeletePrompt = async () => {
    try {
      const selectedPromptTemplate = promptTemplates.find(
        (template) => template.value === selectedPrompt
      );
      if (!selectedPromptTemplate) return;
  
      const response = await fetch(`/api/prompts/${selectedPromptTemplate.id}`, {
        method: "DELETE",
      });
  
      if (!response.ok) throw new Error("Failed to delete prompt");
  
      alert(`${selectedPromptTemplate.label} deleted successfully!`);
      fetchPrompts(); // Refresh the prompts list
    } catch (error) {
      console.error("Error deleting prompt:", error);
    }
  };
  
  const renderContent = () => {
    switch (activeSection) {
      case "Prompts":
        return (
          <Box sx={{ maxHeight: "calc(70vh - 40px)", overflowY: "auto", pr: 2 }}>
            <Typography variant="h6" sx={{ mb: 0, fontWeight: "bold" }}>
              Agent Prompt Settings
              <InputLabel size="string" id="prompt-select-label">Select a Prompt Template</InputLabel>

            </Typography>

            <FormControl fullWidth sx={{ mb: 1 }}>
              <Select
                labelId="prompt-select-label"
                value={selectedPrompt}
                onChange={handlePromptChange}
                sx={{ color: grey[200], mt: 2 }}
              >
                {promptTemplates.map((template) => (
                  <MenuItem key={template.value} value={template.value}>
                    {template.label}
                    {template.isDefault && (
                      <Chip
                        label="Default"
                        size="small"
                        sx={{ ml: 1, bgcolor: "#1976d2", color: "white" }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              sx={{ mb: 3,  color: grey[200] }}
              inputProps={{ style: { color: grey[300] } }}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePrompt}
              >
                Save Prompt
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSetDefault}
              >
                Set as Default
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeletePrompt}
              >
                Delete Prompt
              </Button>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCreateNewPrompt}
              fullWidth
            >
              Create New Prompt
            </Button>
          </Box>
        );
      case "DiscoverySequences":
        return (
          <DiscoverySequenceConfigV2 
            onSave={handleSaveDiscoverySequences}
            onClose={onClose}
            userId={userId}
          />
        );
      case "Personalization":
        return <Typography variant="h6">Personalization Settings</Typography>;
      case "Speech":
        return <Typography variant="h6">Speech Settings</Typography>;
      case "DataControls":
        return <Typography variant="h6">Data Controls</Typography>;
      case "ConnectedApps":
        return <Typography variant="h6">Connected Apps</Typography>;
      case "Security":
        return <Typography variant="h6">Security Settings</Typography>;
      default:
        return null;
    }
  };
  
  const handleSaveDiscoverySequences = async (discoverySequences) => {
    try {
      const result = await updateDiscoverySequences(discoverySequences);
      if (result) {
        alert("Discovery sequences updated successfully!");
      } else {
        alert("Failed to update discovery sequences. Please try again.");
      }
    } catch (error) {
      console.error("Error saving discovery sequences:", error);
      alert("An error occurred while saving discovery sequences.");
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await fetch(`${api_url}/api/prompts`);
      if (!response.ok) throw new Error("Failed to fetch prompts");
      const data = await response.json();
      setPromptTemplates(data);
      const defaultPrompt = data.find((p) => p.isDefault);
      if (defaultPrompt) {
        setSelectedPrompt(defaultPrompt.value);
        setPromptText(defaultPrompt.text);
      } else if (data.length > 0) {
        setSelectedPrompt(data[0].value);
        setPromptText(data[0].text);
      } else {
        setSelectedPrompt("");
        setPromptText("");
      }
    } catch (error) {
      console.error("Error fetching prompts:", error);
    }
  };

  const handlePromptChange = (event) => {
    const selected = event.target.value;
    setSelectedPrompt(selected);

    const selectedTemplate = promptTemplates.find(
      (template) => template.value === selected
    );
    if (selectedTemplate) {
      setPromptText(selectedTemplate.text);
    }
  };

  const handleSavePrompt = async () => {
    try {
      const updatedPrompt = {
        ...promptTemplates.find((template) => template.value === selectedPrompt),
        text: promptText,
      };
      const response = await fetch(`${api_url}/api/prompts/${updatedPrompt.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedPrompt),
      });
      if (!response.ok) throw new Error("Failed to update prompt");
      alert("Prompt saved successfully!");
      fetchPrompts(); // Refresh prompts
    } catch (error) {
      console.error("Error saving prompt:", error);
    }
  };

  const handleCreateNewPrompt = async () => {
    try {
      const newPrompt = {
        label: `New Prompt ${promptTemplates.length + 1}`,
        value: `new_prompt_${promptTemplates.length + 1}`,
        text: "Customize your new prompt here.",
        isDefault: false,
      };
      const response = await fetch(`${api_url}/api/prompts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPrompt),
      });
      if (!response.ok) throw new Error("Failed to create prompt");
      alert("New prompt created successfully!");
      fetchPrompts(); // Refresh prompts
    } catch (error) {
      console.error("Error creating new prompt:", error);
    }
  };

  const handleSetDefault = async () => {
    try {
      const updatedPrompts = promptTemplates.map((template) => ({
        ...template,
        isDefault: template.value === selectedPrompt,
      }));
      const defaultPrompt = updatedPrompts.find((p) => p.value === selectedPrompt);
      const response = await fetch(`${api_url}/api/prompts/${defaultPrompt.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(defaultPrompt),
      });
      if (!response.ok) throw new Error("Failed to set default prompt");
      alert(`${defaultPrompt.label} is now the default prompt!`);
      fetchPrompts(); // Refresh prompts
    } catch (error) {
      console.error("Error setting default prompt:", error);
    }
  };

  return (
    <Box
      display="flex"
      height="70vh"
      width="60vw"
      bgcolor="#1f2937"
      color={grey[300]}
      borderRadius={3}
      overflow="auto"
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: 300,
          bgcolor: "#2d3748",
          p: 3,
          borderRight: "1px solid #4a5568",
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
          Settings
        </Typography>
        <List>
          {sections.map((section) => (
            <ListItemButton
              key={section.value}
              selected={activeSection === section.value}
              onClick={() => setActiveSection(section.value)}
              sx={{
                borderRadius: 2,
                mb: 2,
                "&.Mui-selected": {
                  bgcolor: "#374151",
                  color: "white",
                },
                "&.Mui-selected:hover": {
                  bgcolor: "#4b5563",
                },
              }}
            >
              <ListItemText
                primary={section.label}
                primaryTypographyProps={{ fontWeight: "medium" }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 4 }}>{renderContent()}</Box>
    </Box>
  );
};


export default SettingsMenu;
